import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiSmartphone, FiCode, FiZap, FiShield, FiDatabase, FiSettings } from "react-icons/fi";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const MobileDevelopmentPage = () => {
  usePageMetadata({
    title: 'Mobile Development - Bercastle',
    description: 'iOS and Android mobile app development with beautiful design and robust backend engineering.',
    pageType: 'service',
    content: {
      service: {
        name: "Mobile Development",
        tagline: "Native & Cross-Platform Mobile Apps",
        description: "Beautiful, performant mobile applications for iOS and Android with robust backend engineering.",
        url: "/services/mobile-development"
      },
      platforms: [
        {
          name: "iOS Development",
          description: "Adheres to Apple's highest standards with beautiful design and robust backend engineering.",
          technologies: ["Swift & SwiftUI", "UIKit", "Core Data", "CloudKit integration", "App Store optimization"]
        },
        {
          name: "Android Development",
          description: "Apps that work flawlessly across the diverse Android ecosystem.",
          technologies: ["Kotlin", "Jetpack Compose", "Material Design", "Firebase integration", "Play Store optimization"]
        },
        {
          name: "Cross-Platform Development",
          description: "Reach both iOS and Android users with a single codebase using React Native or Flutter.",
          benefits: ["Faster time to market", "Cost-effective development", "Shared codebase", "Native performance"]
        }
      ],
      features: ["Push notifications", "Offline functionality", "Real-time sync", "In-app purchases", "Social media integration", "Analytics & tracking", "Biometric authentication", "Location services"],
      process: ["Discovery & Planning", "UX/UI Design", "Development & Testing", "App Store Submission", "Launch & Support", "Continuous Updates"],
      value_propositions: [
        "Expert native and cross-platform developers",
        "Beautiful, intuitive designs",
        "Performance-optimized apps",
        "App Store expertise",
        "Ongoing maintenance",
        "Regular updates"
      ],
      meta: {
        source_url: "https://bercastle.com/services/mobile-development",
        service_category: "Mobile Development"
      }
    }
  });
  const platformsData = [
    {
      icon: FiSmartphone,
      title: "iOS Development",
      description: "Our iOS team adheres to Apple's highest standards, blending beautiful design with robust backend engineering to create seamless experiences on iPhone and iPad."
    },
    {
      icon: FiSmartphone,
      title: "Android Development",
      description: "Our experienced Android developers turn your vision into immersive mobile experiences that work seamlessly across the diverse Android ecosystem."
    }
  ];

  const servicesData = [
    {
      icon: FiCode,
      title: "Custom App Development",
      description: "Custom Android and iOS apps development tailored to your business needs"
    },
    {
      icon: FiZap,
      title: "Cross-Platform Apps",
      description: "Native and cross-platform apps development for maximum reach and efficiency"
    },
    {
      icon: FiShield,
      title: "QA & Testing",
      description: "Automated QA and testing services ensuring quality and reliability"
    },
    {
      icon: FiDatabase,
      title: "Backend Integration",
      description: "Power management, notification, and geofencing implementations"
    },
    {
      icon: FiCode,
      title: "Second Platform",
      description: "Second platform app development and porting services"
    },
    {
      icon: FiSmartphone,
      title: "UI/UX Design",
      description: "UI/UX design and prototyping services for exceptional user experience"
    }
  ];

  const appTypesData = [
    {
      icon: FiSmartphone,
      title: "Native Mobile Apps",
      description: "We create high-performance mobile apps for iOS, Android, and Windows to offer superior user experience on each platform with native capabilities."
    },
    {
      icon: FiCode,
      title: "Hybrid Mobile Apps",
      description: "We create cross-platform mobile apps that run on multiple operating systems using frameworks like React Native and Flutter for cost-effective development."
    },
    {
      icon: FiZap,
      title: "Mobile Web Apps",
      description: "Our team includes qualified designers and software engineers with deep expertise in HTML5-based mobile development for responsive web applications."
    },
    {
      icon: FiSettings,
      title: "Embedded & Wearables",
      description: "We create mobile apps integrated with custom wearable devices and smart peripherals, extending your app's capabilities to IoT ecosystems."
    }
  ];

  const technologiesData = [
    {
      icon: FiSmartphone,
      title: "iOS",
      description: "Swift, SwiftUI, Objective-C, Xcode, Core Data"
    },
    {
      icon: FiSmartphone,
      title: "Android",
      description: "Kotlin, Jetpack Compose, Java, Android Studio, Room Database"
    },
    {
      icon: FiCode,
      title: "Cross-Platform",
      description: "React Native, Flutter, Expo, Ionic, Xamarin"
    },
    {
      icon: FiDatabase,
      title: "Backend & APIs",
      description: "Firebase, REST APIs, GraphQL, AWS Amplify, Node.js"
    }
  ];
  return (
    <Layout classmain="mobile-development-page">
      <Seo
        title='Mobile Development - OnScript AI'
        metaDesciption='Build intuitive mobile apps to enhance user engagement and deliver experiences to remember. iOS, Android, and cross-platform development.'
      />
      <Header />

      <ServiceHero 
        badge="Mobile Development"
        title="Create Mobile Experiences"
        titleHighlight="Users Love"
        description="We develop robust mobile applications that provide engaging user experiences across all devices. From concept to launch, we build apps that are fast, secure, and easy to use."
        ctaLink="/contact"
      />

      {/* Platforms Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Mobile Solutions That Rock</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              Our approach combines technology with user-centric design to deliver applications that resonate with users
            </p>
          </div>
          <CardColumn cards={platformsData} cols={2} />
        </div>
      </div>

      {/* Services Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Our Mobile Development Services</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              Comprehensive mobile development from design to deployment
            </p>
          </div>
          <HorizontalScroll items={servicesData} />
        </div>
      </div>

      {/* App Types Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Mobile Apps for Various Platforms</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              From iOS and Android to cross-platform and mobile web apps
            </p>
          </div>
          <CardColumn cards={appTypesData} cols={2} />
        </div>
      </div>

      {/* Technologies Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Technologies We Use</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              Modern mobile development stack
            </p>
          </div>
          <CardColumn cards={technologiesData} cols={4} />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Build Your Mobile App?"
        description="Whether you need a native or cross-platform solution, our expertise ensures your app meets business goals and stands out in the market."
        buttonText="Start Your Mobile Project"
      />

      <Footer />
    </Layout>
  )
}

export default MobileDevelopmentPage
